import { useCallback, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import type { ChildPage, Page, PortalSchema } from "../../shared/schema";
import { LEGACY_ROUTE_SLUGS } from "./useLegacyRouteResolver";

interface UsePageRoutingResult {
  /** The currently active page based on URL or first page (may be a top-level or nested sub-page) */
  currentPage: Page | ChildPage | null;
  /** The current page ID */
  currentPageId: string | null;
  /** The top-level page that owns currentPage - itself when currentPage is top-level */
  currentTopLevelPage: Page | null;
  /** Navigate to a page by ID (top-level or nested) - updates the URL, optionally with query params (e.g. deep-linking the global cart icon into a shop block's cart step) */
  navigateToPage: (pageId: string, queryParams?: Record<string, string>) => void;
  /** Navigate to a top-level page by slug - updates the URL */
  navigateToSlug: (slug: string) => void;
  /** Whether the current URL matches a valid page */
  isValidRoute: boolean;
}

// Builds the URL path for a top-level page and optional nested child page
function buildPagePath(firstPageSlug: string | undefined, page: Page, child?: ChildPage): string {
  const parentSegment = page.slug === firstPageSlug ? "" : `/${page.slug}`;
  const base = parentSegment || "/";
  if (!child) return base;
  return `${base === "/" ? "" : base}/${child.slug}`;
}

/**
 * Hook for URL-based page routing in the enduser portal.
 *
 * Reads the current page slug(s) from URL params and provides navigation functions
 * that update the URL. This enables deep linking, browser back/forward navigation,
 * and shareable URLs.
 *
 * URL Structure:
 * - "/" -> First page (default)
 * - "/{slug}" -> Top-level page with matching slug
 * - "/{slug}/{subSlug}" -> Sub-page nested one level under the matching top-level page
 *
 * @param schema - The portal schema containing pages, or null if not yet loaded
 * @returns Routing state and navigation functions
 */
export function usePageRouting(schema: PortalSchema | null): UsePageRoutingResult {
  const navigate = useNavigate();
  const { pageSlug, subPageSlug } = useParams<{ pageSlug?: string; subPageSlug?: string }>();

  // Determine current page based on URL slug(s)
  const { currentPage, currentTopLevelPage, isValidRoute } = useMemo(() => {
    if (!schema || schema.pages.length === 0) {
      return { currentPage: null, currentTopLevelPage: null, isValidRoute: true };
    }

    const firstPage = schema.pages[0] ?? null;

    // Root URL "/" - show first page
    if (!pageSlug) {
      return { currentPage: firstPage, currentTopLevelPage: firstPage, isValidRoute: true };
    }

    // Find top-level page by slug
    const foundPage = schema.pages.find((p) => p.slug === pageSlug);

    if (!foundPage) {
      // Invalid slug - return first page but mark as invalid for redirect
      return { currentPage: firstPage, currentTopLevelPage: firstPage, isValidRoute: false };
    }

    if (!subPageSlug) {
      return { currentPage: foundPage, currentTopLevelPage: foundPage, isValidRoute: true };
    }

    // Legacy routes have no sub-pages of their own - an extra segment there is invalid
    if (LEGACY_ROUTE_SLUGS.includes(pageSlug as (typeof LEGACY_ROUTE_SLUGS)[number])) {
      return { currentPage: foundPage, currentTopLevelPage: foundPage, isValidRoute: false };
    }

    const foundChild = (foundPage.pages ?? []).find((c) => c.slug === subPageSlug);
    if (!foundChild) {
      return { currentPage: foundPage, currentTopLevelPage: foundPage, isValidRoute: false };
    }

    return { currentPage: foundChild, currentTopLevelPage: foundPage, isValidRoute: true };
  }, [schema, pageSlug, subPageSlug]);

  // Navigate to a page by ID (top-level or nested) - updates the URL
  const navigateToPage = useCallback(
    (pageId: string, queryParams?: Record<string, string>) => {
      if (!schema) return;

      const firstPageSlug = schema.pages[0]?.slug;
      const search = queryParams ? `?${new URLSearchParams(queryParams).toString()}` : "";

      const topLevelMatch = schema.pages.find((p) => p.id === pageId);
      if (topLevelMatch) {
        navigate(`${buildPagePath(firstPageSlug, topLevelMatch)}${search}`);
        return;
      }

      for (const page of schema.pages) {
        const child = page.pages?.find((c) => c.id === pageId);
        if (child) {
          navigate(`${buildPagePath(firstPageSlug, page, child)}${search}`);
          return;
        }
      }
    },
    [schema, navigate]
  );

  // Navigate to a top-level page by slug - updates the URL
  const navigateToSlug = useCallback(
    (slug: string) => {
      if (!schema) return;

      const firstPage = schema.pages[0];
      // Use root "/" for first page, otherwise use the slug
      const targetPath = slug === firstPage?.slug ? "/" : `/${slug}`;
      navigate(targetPath);
    },
    [schema, navigate]
  );

  return {
    currentPage,
    currentPageId: currentPage?.id ?? null,
    currentTopLevelPage,
    navigateToPage,
    navigateToSlug,
    isValidRoute,
  };
}
